import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {RightSidebarFloatingPanel} from "./RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "./RightSidebarPanelHeader";

export interface RadioFilterOption<TValue extends string = string> {
    value: TValue
    label: string
    /** A dot after the label, as the restaurant picker shows a restaurant's tag colour. */
    color?: string
}

export interface RadioFilterPanelProps<TValue extends string> {
    title: string
    options: readonly RadioFilterOption<TValue>[]
    value: TValue
    onChange: (value: TValue) => void
    onReset?: () => void
    onClose?: () => void
}

export function RadioFilterPanel<TValue extends string>({
    title,
    options,
    value,
    onChange,
    onReset,
    onClose,
}: RadioFilterPanelProps<TValue>) {
    const {t} = useTranslation();

    return (
        <RightSidebarFloatingPanel>
            <RightSidebarPanelHeader title={title} onClose={onClose}/>

            <Stack sx={{px: 2, pb: 2, overflowY: "auto", minHeight: 0}}>
                <RadioGroup
                    value={value}
                    onChange={event => onChange(event.target.value as TValue)}
                >
                    {options.map(option => {
                        const selected = option.value === value;

                        return (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio sx={{p: 0.5, mr: 0.5}}/>}
                                label={option.color ? (
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{minWidth: 0, pr: 1}}>
                                        <Typography sx={{minWidth: 0}}>{option.label}</Typography>
                                        <Box
                                            aria-hidden
                                            sx={{width: 14, height: 14, flexShrink: 0, borderRadius: "50%", bgcolor: option.color}}
                                        />
                                    </Stack>
                                ) : <Typography>{option.label}</Typography>}
                                sx={{
                                    m: 0,
                                    // Lets a long label wrap inside the panel instead of pushing the dot past its edge.
                                    minWidth: 0,
                                    px: 0.75,
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: selected ? "action.selected" : "transparent",
                                    "&:hover": {backgroundColor: "action.hover"},
                                    "& .MuiFormControlLabel-label": {flex: 1, minWidth: 0},
                                }}
                            />
                        );
                    })}
                </RadioGroup>

                {onReset && (
                    <Button
                        type="button"
                        size="small"
                        color="inherit"
                        onClick={onReset}
                        sx={{
                            alignSelf: "flex-start",
                            mt: 1,
                            ml: 0.25,
                            minHeight: 28,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {t("Reset")}
                    </Button>
                )}
            </Stack>
        </RightSidebarFloatingPanel>
    );
}
